import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Radar, ArrowRight, XCircle } from "lucide-react";
import { pollCheckoutStatus } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * After Stripe Checkout redirects here with ?session_id=cs_test_…, we poll
 * the backend /billing/status/{session_id} endpoint until it returns paid.
 * The poll doubles as a webhook fallback — if the webhook is delayed, the
 * backend queries Stripe directly on the first status call.
 */
export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [state, setState] = useState("checking"); // checking | paid | failed
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let alive = true;

    async function tick() {
      if (!alive) return;
      attempts.current += 1;
      try {
        const r = await pollCheckoutStatus(sessionId);
        if (!alive) return;
        if (r.payment_status === "paid" || r.status === "completed") {
          // Refresh the AuthContext so `subscription_status` on `user` flips
          // to "trialing"/"active" before we route into /feed.
          await refresh();
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(r.payment_status)) {
          setState("failed");
          return;
        }
      } catch (err) {
        if (attempts.current >= 30) {
          setState("failed");
          return;
        }
      }
      if (attempts.current >= 30) {
        setState("failed");
        return;
      }
      setTimeout(tick, 2000);
    }
    tick();
    return () => {
      alive = false;
    };
  }, [sessionId, refresh]);

  return (
    <div
      data-testid="payment-success-page"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0b1120] px-6"
    >
      <div className="w-full max-w-sm border border-[#1e293b] bg-[#111827] p-6 text-center">
        <div className="mb-4 flex justify-center">
          <div className="flex h-9 w-9 items-center justify-center border border-[#3b82f6]/40 bg-[#3b82f6]/10">
            <Radar className="h-4 w-4 text-[#3b82f6]" />
          </div>
        </div>
        {state === "checking" && (
          <>
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#3b82f6]" />
            <div className="mt-4 text-base font-bold text-[#f8fafc]">Confirming your trial…</div>
            <div className="mt-1 text-sm text-[#94a3b8]">
              Waiting on Stripe. This usually resolves in a few seconds.
            </div>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-8 w-8 text-[#10b981]" />
            <div className="mt-4 text-lg font-bold text-[#f8fafc]">You're in.</div>
            <div className="mt-1 text-sm text-[#94a3b8]">
              Your 14-day free trial is active. Cancel any time before day 15.
            </div>
            <button
              type="button"
              onClick={() => navigate("/feed", { replace: true })}
              data-testid="payment-success-continue"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 border border-[#3b82f6] bg-[#3b82f6] px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#2563eb]"
            >
              Open Signal Edge <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="mx-auto h-8 w-8 text-[#f43f5e]" />
            <div className="mt-4 text-lg font-bold text-[#f8fafc]">
              Couldn't confirm your trial
            </div>
            <div className="mt-1 text-sm text-[#94a3b8]">
              If Stripe charged you, refresh your account page — it may just be a delayed webhook.
            </div>
            <Link
              to="/account"
              data-testid="payment-success-account"
              className="mt-5 inline-flex w-full items-center justify-center gap-2 border border-[#1e293b] bg-[#0f172a] px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-[#f8fafc] transition-colors hover:border-[#334155]"
            >
              Go to account
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div
      data-testid="payment-cancel-page"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0b1120] px-6"
    >
      <div className="w-full max-w-sm border border-[#1e293b] bg-[#111827] p-6 text-center">
        <XCircle className="mx-auto h-8 w-8 text-[#94a3b8]" />
        <div className="mt-4 text-lg font-bold text-[#f8fafc]">No charge, no signup.</div>
        <div className="mt-1 text-sm text-[#94a3b8]">
          You cancelled checkout. Your account is still there whenever you want to come back.
        </div>
        <Link
          to="/paywall"
          data-testid="payment-cancel-retry"
          className="mt-5 inline-flex w-full items-center justify-center gap-2 border border-[#3b82f6] bg-[#3b82f6] px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#2563eb]"
        >
          Try again
        </Link>
        <Link
          to="/"
          data-testid="payment-cancel-home"
          className="mt-2 inline-block text-[11px] text-[#64748b] hover:text-[#94a3b8]"
        >
          ← Back to home
        </Link>
      </div>
    </div>
  );
}
